import React from 'react';
import styled from 'styled-components';
import { Organization } from '../types';

import {
  COLORS,
  BREAKPOINTS,
  SwitcherMenuItem,
  ComponentLStyling,
  ComponentMStyling,
  ComponentTextStyle,
  ComponentXXSStyling,  
  IconButton,
  Button,
  SystemIcons,
  Size,
} from '@laerdal/life-react-components';

import { SwitchApp, LaerdalLogo } from './Icons';
import { WithTranslation, withTranslation } from 'react-i18next';
import { MenuItem } from './helpers';

export const SwitcherWrapper = styled.aside`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  background: white;
  width: 100%;
  padding-top: 8px;

  ${BREAKPOINTS.MEDIUM} {
    padding-right: 32px;

    footer {
      margin-right: -32px;
    }
  }

  ${BREAKPOINTS.LARGE} {
    padding-right: 56px;

    footer {
      margin-right: -56px;
    }
  }
`;

const SwitcherContent = styled.div`
  flex: 1;
  overflow-y: auto;

  /* width */

  ::-webkit-scrollbar {
    width: 4px;
    margin: 0 6px;
    cursor: pointer;
  }

  /* Track */

  ::-webkit-scrollbar-track {
    background: ${COLORS.neutral_100};
    border-radius: 8px;
  }

  /* Handle */

  ::-webkit-scrollbar-thumb {
    background: ${COLORS.neutral_600};
    border-radius: 8px;
  }

  /* Handle on hover */

  ::-webkit-scrollbar-thumb:hover {
    background: ${COLORS.neutral_500};
  }
`;

const SwitcherHeader = styled.header`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  padding-left: 24px;
  order: -1;

  h2 {
    margin: 0;
    ${ComponentMStyling(ComponentTextStyle.Bold, COLORS.neutral_600)}
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const SwitcherDescription = styled.p`
  margin-top: 8px;
  margin-left: 24px;
  margin-right: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${COLORS.neutral_200};
  color: ${COLORS.neutral_600};
`;

const SwitcherTitle = styled.h3`
  margin-top: 10px;
  margin-bottom: 8px;
  padding: 0 24px;
  display: flex;
  align-items: center;
  ${ComponentLStyling(ComponentTextStyle.Bold, COLORS.neutral_500)}
`;

const SwitcherFooter = styled.footer`
  background-color: ${COLORS.neutral_20};
  height: 64px;
  border-top: 1px solid ${COLORS.neutral_200};
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0 24px;

  span {
    ${ComponentXXSStyling(ComponentTextStyle.Regular, COLORS.neutral_500)}
  }
`;

const HorizontalLayout = styled.div`
  display: flex;
  flex-direction: column;
`;

const NavContainer = styled.div`
  justify-content: center;
  align-items: center;
`;

const SwitcherPinnedContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: 10px;
  padding-bottom: 8px;
  margin-top: 8px;
`;

const Border = styled.div`
  margin: -10px 24px 10px;
  border-top: 1px solid ${COLORS.neutral_200};
`;

export interface SwitcherProps extends WithTranslation {
  loggedIn: boolean;
  firstName: string | undefined;
  lastName: string | undefined;
  email: string | undefined;
  organization: Organization | undefined;
  token: string | undefined;
  services: MenuItem[] | undefined;
  hide: () => void;
  version?: string;
  canSwitchCurrentOrganization: boolean;
  onShowChangeOrganizationModal: () => void;
}

class _Switcher extends React.Component<SwitcherProps> {
  constructor(props: SwitcherProps) {
    super(props);
  }

  render() {
    const { t } = this.props;

    const headerText = this.props?.organization?.name || t('Switch to...');

    const pinnedServices = this.props?.services?.filter((service) => service.pinned);
    const normalServices = this.props?.services?.filter((service) => !service.pinned);

    return (
      <SwitcherWrapper id={'switcherContentContainer'}>
        {!!this.props?.organization && <SwitcherDescription>{t('As a member of this organization, you can use the apps and websites listed below.')}</SwitcherDescription>}
        <SwitcherContent>
          {!!this.props?.organization && <SwitcherTitle>{t('Switch to...')}</SwitcherTitle>}
          <HorizontalLayout>
            {!!normalServices && (
              <NavContainer>
                {normalServices?.map((item, index) => (
                  <SwitcherMenuItem key={index} label={item.label} icon={item.icon} url={item.to} />
                ))}
              </NavContainer>
            )}
            {!this.props.services?.length && <p>{t('Nothing to show here.')}</p>}
          </HorizontalLayout>
        </SwitcherContent>

        {!!pinnedServices?.length && (
          <SwitcherPinnedContent>
            <Border />
            {pinnedServices?.map((item, index) => (
              <SwitcherMenuItem key={index} label={item.label} icon={item.icon} url={item.to} />
            ))}
          </SwitcherPinnedContent>
        )}

        {/*moved below menu items for tabbing order*/}
        <SwitcherHeader>
          <HeaderContainer>
            <h2>{headerText}</h2>
            {this.props.canSwitchCurrentOrganization && (
              <Button
                size={Size.Small}
                variant="secondary"
                icon={<SystemIcons.Institute size="20px" />}
                onClick={() => {
                  this.props.onShowChangeOrganizationModal();
                }}>
                {t('Change organization')}
              </Button>
            )}
          </HeaderContainer>

          <IconButton id={'globalNavAppsCloseButton'} action={() => this.props.hide()} variant={'secondary'} shape={'circular'}>
            <SwitchApp />
          </IconButton>
        </SwitcherHeader>

        <SwitcherFooter>
          <LaerdalLogo />

          {!!this.props?.version && <span>{this.props?.version}</span>}
        </SwitcherFooter>
      </SwitcherWrapper>
    );
  }
}

export const Switcher = withTranslation()(_Switcher);
